/* eslint-disable react-hooks/exhaustive-deps */
import { FilterMenuOption } from 'pages/Navigation/Map/FilterMenu';
import {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
} from 'react';
import { getHeatmapService, useOccCoords } from 'services/CordsService';

import { socket } from "hooks/useSocket";

import * as GlobalMapHelpers from './helpers';

import { initialFilterStateList } from './data';

import {
  GlobalMapFunctions,
  GlobalMapValues,
  HeatmapType,
  IFilterCount,
  IFilterOptions,
  IHeatmapMarker,
  IMapCoords,
  IMapLocationData,
  ISocketMarkerList
} from './types';

import { useAuth } from 'contexts/authContext';
import { treatSocketLocations } from 'utils/treatSocketLocations';
import { useHistory } from 'react-router-dom';

export const GlobalMapContext = createContext({} as GlobalMapValues.Context);

export function GlobalMapProvider({ children }: GlobalMapValues.Provider) {
  const location = useHistory();

  const { tenant } = useAuth();

  const [markerFilters, setMarkerFilters] = useState<IFilterOptions>(initialFilterStateList);
  const [heatmaps, setHeatmaps] = useState<IHeatmapMarker[]>([]);

  const [selectedMarker, setSelectedMarker] = useState<IMapLocationData>();

  const [activeHeatmap, setActiveHeatmap] = useState<HeatmapType>();

  const [occurrenceLocations, setOccurrenceLocations] = useState<IMapLocationData[]>([]);
  const [policemanLocations, setPolicemanLocations] = useState<IMapLocationData[]>([]);

  const [searchString, setSearchString] = useState<string>();

  const [mapListTab, setMapListTab] = useState<number>(0);

  const mapRef = useRef<L.Map>(null);

  const occurrencesLocations = useOccCoords().data;

  const markers = GlobalMapHelpers.findBySearchOrFilter({
    markerFilters,
    markers: [...occurrenceLocations, ...policemanLocations],
    searchString
  })

  const favoriteMarkers = policemanLocations.filter((el) => el.isFavorite === true);

  const originHeatmaps = heatmaps.map((el) => {
    if (!el.originCoords) return null;

    return [el.originCoords[0], el.originCoords[1], 1.31231]
  }).filter(Boolean) as [number, number, number][];

  const foundHeatmaps = heatmaps.map((el) => {
    if (!el.findCoords) return null;

    return [el.findCoords[0], el.findCoords[1], 4.1241]
  }).filter(Boolean) as [number, number, number][];

  function handleMapCenterChange(coords: IMapCoords) {
    mapRef.current?.setView(
      { lat: coords.lat, lng: coords.lng },
      13
    );
  }

  function handleFilterClick(filter: FilterMenuOption) {
    const _filters = markerFilters;
    _filters[filter.id as keyof IFilterOptions] = filter;
    setMarkerFilters({ ..._filters });
  }

  function handleFiltersCount(count: IFilterCount) {
    let filters: IFilterOptions = {} as IFilterOptions;
    Object.keys(markerFilters).forEach((key) => {
      filters[key as keyof IFilterOptions] = markerFilters[key as keyof IFilterOptions];
      filters[key as keyof IFilterOptions].value = count[key as keyof IFilterOptions];
    });

    setMarkerFilters({ ...filters })
  }

  function handleMarkerClick(marker: IMapLocationData | undefined) {
    setSelectedMarker(marker);
  }

  function handleTabChange(index: number) {
    setMapListTab(index);
  }

  function handleSearchTextChange(search: string) {
    setSearchString(search);
  }

  function handleFavoriteClick({
    location,
    type
  }: GlobalMapFunctions.HandleFavoriteClickParams) {
    location.isFavorite = !location.isFavorite;

    let _locations = type === 'occurrence' ? [...occurrenceLocations] : [...policemanLocations];
    const index = _locations.indexOf(location);

    if (index !== 1) {
      _locations[index] = location;

      GlobalMapHelpers.handleFavoriteMarkerStorage(
        location.dataId,
        location.isFavorite
      );

      type === 'occurrence'
        ? setOccurrenceLocations([..._locations])
        : setPolicemanLocations([..._locations]);
    }
  }

  function onLocationChange(socketMarkers: ISocketMarkerList) {
    const _locations = treatSocketLocations({
      socketLocations: socketMarkers,
      tenantId: tenant?.id ?? '',
    })

    const policemanLocations =
      GlobalMapHelpers.mountPolicemanLocationMarker(_locations);

    setPolicemanLocations([...policemanLocations]);
  }

  async function findOccurrenceLocations() {
    const occurrences = occurrencesLocations?.items;
    if (occurrences && occurrences.length) {
      const { occurrencesMarkers, countValues } =
        await GlobalMapHelpers.mountOccurrencesLocationMarker(occurrences);

      handleFiltersCount(countValues);

      setOccurrenceLocations(occurrencesMarkers);
    }
  }

  function handleHeatmapClick(heatmapType?: HeatmapType) {
    setActiveHeatmap(heatmapType);
  }

  function handleMoreDetailClick(occurrenceId: string) {
    location.push(`/map/${occurrenceId}`);
  }

  async function findHeatmapLocations() {
    const response = await getHeatmapService();

    if (response.status) {
      const _heatmaps = response.data.items;
      setHeatmaps(_heatmaps);
    }
  }

  useEffect(() => {
    findOccurrenceLocations();
  }, [occurrencesLocations]);

  useEffect(() => {
    if (activeHeatmap !== undefined) {
      findHeatmapLocations();
    }
  }, [activeHeatmap]);

  useEffect(() => {
    if (tenant?.id) {
      socket.on("onLocationChange", onLocationChange);

      socket.emit("listLocations", tenant.id);
      socket.on("listLocations", onLocationChange);
    }

    return () => {
      socket.off("onLocationChange");
      socket.off("listLocations");
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <GlobalMapContext.Provider
      value={{
        markers,
        activeHeatmap,
        originHeatmaps,
        foundHeatmaps,
        favoriteMarkers,
        handleHeatmapClick,
        mapRef,
        mapListTab,
        locations: [...occurrenceLocations, ...policemanLocations],
        selectedMarker,
        markerFilters,
        handleMarkerClick,
        handleMapCenterChange,
        handleFilterClick,
        handleFavoriteClick,
        handleTabChange,
        handleSearchTextChange,
        handleMoreDetailClick
      }}
    >
      {children}
    </GlobalMapContext.Provider>
  )
};

export function useGlobalMapActions() {
  return useContext(GlobalMapContext);
}